import {
  useRef,
  useState,
  type FormEvent,
} from 'react'

import {
  FormsConfigurationError,
  getRecaptchaSiteKey,
} from '../config/api'

import {
  describedBy,
  formLimits,
  type FormFieldErrors,
} from '../config/formLimits'

import {
  FORMS_SITE_NAME,
} from '../config/formsSite'

import {
  FormsApiError,
  submitInquiryForm,
} from '../services/formsApi'

import {
  containsWebLink,
  isSuspiciousMessage,
  isValidEmail,
  normalizePhone,
  trimmed,
} from '../utils/formValidation'

import {
  CharacterCounter,
  FieldError,
} from './FormFieldFeedback'

import {
  Recaptcha,
} from './Recaptcha'

const serviceOptions = [
  'Hair services',
  'Facials and skincare',
  'Esthetician services',
  'Waxing and eyebrow shaping',
  'Lash services',
  'Makeup services',
  'Massage and relaxation',
  'Body treatments',
  'Other',
]

type Feedback =
  | {
      type:
        | 'success'
        | 'error'

      message:
        string
    }
  | null

export function InquiryForm() {
  const [
    serviceType,
    setServiceType,
  ] =
    useState('')

  const [
    messageLength,
    setMessageLength,
  ] =
    useState(0)

  const [
    isSubmitting,
    setIsSubmitting,
  ] =
    useState(false)

  const [
    feedback,
    setFeedback,
  ] =
    useState<Feedback>(
      null,
    )

  const [
    fieldErrors,
    setFieldErrors,
  ] =
    useState<FormFieldErrors>(
      {},
    )

  const [
    captchaToken,
    setCaptchaToken,
  ] =
    useState('')

  const [
    captchaResetKey,
    setCaptchaResetKey,
  ] =
    useState(0)

  const submitLock =
    useRef(false)

  const recaptchaSiteKey =
    getRecaptchaSiteKey()

  const clearFieldError =
    (
      field:
        string,
    ) => {
      setFieldErrors(
        (current) => {
          if (
            !current[
              field
            ]
          ) {
            return current
          }

          const next = {
            ...current,
          }

          delete next[
            field
          ]

          return next
        },
      )
    }

  const submit =
    async (
      event:
        FormEvent<HTMLFormElement>,
    ) => {
      event.preventDefault()

      if (
        isSubmitting ||
        submitLock.current
      ) {
        return
      }

      const form =
        event.currentTarget

      const consentInput =
        form.elements.namedItem(
          'consent',
        ) as
          | HTMLInputElement
          | null

      if (
        !consentInput
          ?.checked
      ) {
        consentInput
          ?.focus()

        setFeedback({
          type:
            'error',

          message:
            'Please agree to the Privacy Policy and contact consent before submitting.',
        })

        return
      }

      const data =
        new FormData(
          form,
        )

      const name =
        trimmed(
          data.get(
            'name',
          ),
        )

      const email =
        trimmed(
          data.get(
            'email',
          ),
        ).toLowerCase()

      const rawPhone =
        trimmed(
          data.get(
            'phone',
          ),
        )

      const phone =
        normalizePhone(
          rawPhone,
        )

      const selectedService =
        trimmed(
          data.get(
            'serviceType',
          ),
        )

      const otherServiceDescription =
        trimmed(
          data.get(
            'otherServiceDescription',
          ),
        )

      const preferredContactMethod =
        trimmed(
          data.get(
            'preferredContactMethod',
          ),
        )

      const message =
        trimmed(
          data.get(
            'message',
          ),
        )

      const website =
        trimmed(
          data.get(
            'website',
          ),
        )

      const errors:
        FormFieldErrors =
        {}

      if (!name) {
        errors.name =
          'Name is required.'
      } else if (
        name.length >
        formLimits.name
      ) {
        errors.name =
          `Name must be ${formLimits.name} characters or fewer.`
      } else if (
        name.length < 2
      ) {
        errors.name =
          'Name must contain at least 2 characters.'
      }

      if (!email) {
        errors.email =
          'Email is required.'
      } else if (
        email.length >
        formLimits.email
      ) {
        errors.email =
          `Email must be ${formLimits.email} characters or fewer.`
      } else if (
        !isValidEmail(
          email,
        )
      ) {
        errors.email =
          'Enter a valid email address.'
      }

      if (
        rawPhone.length >
        formLimits.phone
      ) {
        errors.phone =
          `Phone must be ${formLimits.phone} characters or fewer.`
      } else if (
        phone === null
      ) {
        errors.phone =
          'Enter a valid phone number.'
      }

      if (
        !selectedService
      ) {
        errors.serviceType =
          'Service type is required.'
      } else if (
        selectedService.length >
        formLimits.serviceType
      ) {
        errors.serviceType =
          `Service type must be ${formLimits.serviceType} characters or fewer.`
      } else if (
        !serviceOptions.includes(
          selectedService,
        )
      ) {
        errors.serviceType =
          'Select a valid service type.'
      }

      if (
        !preferredContactMethod
      ) {
        errors.preferredContactMethod =
          'Preferred contact method is required.'
      } else if (
        preferredContactMethod.length >
        formLimits.preferredContactMethod
      ) {
        errors.preferredContactMethod =
          `Preferred contact method must be ${formLimits.preferredContactMethod} characters or fewer.`
      } else if (
        ![
          'Email',
          'Phone',
          'Text',
        ].includes(
          preferredContactMethod,
        )
      ) {
        errors.preferredContactMethod =
          'Select a valid contact method.'
      }

      if (
        [
          'Phone',
          'Text',
        ].includes(
          preferredContactMethod,
        ) &&
        !phone
      ) {
        errors.phone =
          'Phone is required for phone or text replies.'
      }

      if (
        selectedService ===
        'Other'
      ) {
        if (
          !otherServiceDescription
        ) {
          errors.otherServiceDescription =
            'A service description is required.'
        } else if (
          otherServiceDescription.length >
          formLimits.otherServiceDescription
        ) {
          errors.otherServiceDescription =
            `Service description must be ${formLimits.otherServiceDescription} characters or fewer.`
        } else if (
          otherServiceDescription.length <
          3
        ) {
          errors.otherServiceDescription =
            'Service description must contain at least 3 characters.'
        } else if (
          containsWebLink(
            otherServiceDescription,
          )
        ) {
          errors.otherServiceDescription =
            'Service description cannot contain links.'
        }
      }

      if (!message) {
        errors.message =
          'Inquiry details are required.'
      } else if (
        message.length >
        formLimits.message
      ) {
        errors.message =
          `Inquiry details must be ${formLimits.message.toLocaleString()} characters or fewer.`
      } else if (
        message.length < 10
      ) {
        errors.message =
          'Inquiry details must contain at least 10 characters.'
      } else if (
        isSuspiciousMessage(
          message,
        )
      ) {
        errors.message =
          'Please remove excessive links or repeated text.'
      }

      if (
        !recaptchaSiteKey
      ) {
        errors.recaptcha =
          'Human verification is unavailable.'
      } else if (
        !captchaToken
      ) {
        errors.recaptcha =
          'Please complete the security check.'
      }

      if (
        Object.keys(
          errors,
        ).length >
        0
      ) {
        setFieldErrors(
          errors,
        )

        setFeedback({
          type:
            'error',

          message:
            'Please review the marked fields and complete the security check.',
        })

        return
      }

      submitLock.current =
        true

      setIsSubmitting(
        true,
      )

      setFieldErrors(
        {},
      )

      setFeedback(
        null,
      )

      try {
        await submitInquiryForm({
          formType:
            'inquiry',

          websiteName:
            FORMS_SITE_NAME,

          name,

          email,

          phone:
            phone ?? '',

          serviceType:
            selectedService,

          otherServiceDescription:
            selectedService ===
            'Other'
              ? otherServiceDescription
              : '',

          preferredContactMethod,

          message,

          pageUrl:
            window.location.href,

          submittedAt:
            new Date()
              .toISOString(),

          website,

          consent:
            consentInput.checked,

          recaptchaToken:
            captchaToken,
        })

        form.reset()

        setServiceType(
          '',
        )

        setMessageLength(
          0,
        )

        setCaptchaToken(
          '',
        )

        setCaptchaResetKey(
          (value) =>
            value + 1,
        )

        setFeedback({
          type:
            'success',

          message:
            'Thank you. Your inquiry was sent successfully, and the salon will follow up soon.',
        })
      } catch (error) {
        setCaptchaToken(
          '',
        )

        setCaptchaResetKey(
          (value) =>
            value + 1,
        )

        if (
          error instanceof
            FormsApiError ||
          error instanceof
            FormsConfigurationError
        ) {
          setFeedback({
            type:
              'error',

            message:
              error.userMessage,
          })

          setFieldErrors(
            error instanceof
              FormsApiError
              ? error.fieldErrors ??
                  {}
              : {},
          )
        } else {
          setFeedback({
            type:
              'error',

            message:
              'We could not send your inquiry. Please try again or contact the salon directly.',
          })
        }
      } finally {
        submitLock.current =
          false

        setIsSubmitting(
          false,
        )
      }
    }

  return (
    <section
      className="service-inquiry"
      aria-labelledby="service-inquiry-title"
    >
      <div className="service-inquiry__intro reveal reveal--heading">
        <span className="eyebrow eyebrow--light">
          Current Availability
        </span>

        <h2 id="service-inquiry-title">
          Ask about the right service for you.
        </h2>

        <p>
          Services vary by the independent professionals currently working at Diamond Salon. Send an inquiry and the salon can help confirm current offerings and the best next step.
        </p>
      </div>

      <form
        className="contact-form contact-form--dark reveal reveal--delay-1"
        onSubmit={submit}
        aria-busy={
          isSubmitting
        }
        noValidate
      >
        <p className="form-required-note">
          <span aria-hidden="true">
            *
          </span>{' '}
          Required fields
        </p>

        <div className="field-row">
          <label>
            <span className="field-label">
              Name{' '}
              <span aria-hidden="true">
                *
              </span>
            </span>

            <input
              name="name"
              type="text"
              autoComplete="name"
              minLength={2}
              maxLength={
                formLimits.name
              }
              required
              aria-invalid={
                Boolean(
                  fieldErrors.name,
                )
              }
              aria-describedby={
                fieldErrors.name
                  ? 'inquiry-name-error'
                  : undefined
              }
              onChange={() =>
                clearFieldError(
                  'name',
                )
              }
            />

            <FieldError
              id="inquiry-name-error"
              message={
                fieldErrors.name
              }
            />
          </label>

          <label>
            <span className="field-label">
              Email{' '}
              <span aria-hidden="true">
                *
              </span>
            </span>

            <input
              name="email"
              type="email"
              autoComplete="email"
              maxLength={
                formLimits.email
              }
              required
              aria-invalid={
                Boolean(
                  fieldErrors.email,
                )
              }
              aria-describedby={
                fieldErrors.email
                  ? 'inquiry-email-error'
                  : undefined
              }
              onChange={() =>
                clearFieldError(
                  'email',
                )
              }
            />

            <FieldError
              id="inquiry-email-error"
              message={
                fieldErrors.email
              }
            />
          </label>
        </div>

        <label>
          <span className="field-label">
            Phone{' '}
            <span className="field-optional">
              Required for phone or text replies
            </span>
          </span>

          <input
            name="phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            maxLength={
              formLimits.phone
            }
            aria-invalid={
              Boolean(
                fieldErrors.phone,
              )
            }
            aria-describedby={
              fieldErrors.phone
                ? 'inquiry-phone-error'
                : undefined
            }
            onChange={() =>
              clearFieldError(
                'phone',
              )
            }
          />

          <FieldError
            id="inquiry-phone-error"
            message={
              fieldErrors.phone
            }
          />
        </label>

        <div className="field-row">
          <label>
            <span className="field-label">
              Service type{' '}
              <span aria-hidden="true">
                *
              </span>
            </span>

            <select
              name="serviceType"
              value={
                serviceType
              }
              onChange={(
                event,
              ) => {
                setServiceType(
                  event.target
                    .value,
                )

                clearFieldError(
                  'serviceType',
                )

                clearFieldError(
                  'otherServiceDescription',
                )
              }}
              required
              aria-invalid={
                Boolean(
                  fieldErrors.serviceType,
                )
              }
              aria-describedby={
                fieldErrors.serviceType
                  ? 'inquiry-service-error'
                  : undefined
              }
            >
              <option value="">
                Select a service
              </option>

              {serviceOptions.map(
                (
                  option,
                ) => (
                  <option
                    key={
                      option
                    }
                  >
                    {
                      option
                    }
                  </option>
                ),
              )}
            </select>

            <FieldError
              id="inquiry-service-error"
              message={
                fieldErrors.serviceType
              }
            />
          </label>

          <label>
            <span className="field-label">
              Preferred contact{' '}
              <span aria-hidden="true">
                *
              </span>
            </span>

            <select
              name="preferredContactMethod"
              defaultValue="Email"
              required
              aria-invalid={
                Boolean(
                  fieldErrors.preferredContactMethod,
                )
              }
              aria-describedby={
                fieldErrors.preferredContactMethod
                  ? 'inquiry-contact-method-error'
                  : undefined
              }
              onChange={() =>
                clearFieldError(
                  'preferredContactMethod',
                )
              }
            >
              <option>
                Email
              </option>

              <option>
                Phone
              </option>

              <option>
                Text
              </option>
            </select>

            <FieldError
              id="inquiry-contact-method-error"
              message={
                fieldErrors.preferredContactMethod
              }
            />
          </label>
        </div>

        {serviceType ===
          'Other' && (
          <label>
            <span className="field-label">
              Please describe your service{' '}
              <span aria-hidden="true">
                *
              </span>{' '}
              <span className="field-optional">
                No links
              </span>
            </span>

            <input
              name="otherServiceDescription"
              type="text"
              minLength={3}
              maxLength={
                formLimits.otherServiceDescription
              }
              required
              aria-invalid={
                Boolean(
                  fieldErrors.otherServiceDescription,
                )
              }
              aria-describedby={
                fieldErrors.otherServiceDescription
                  ? 'inquiry-other-service-error'
                  : undefined
              }
              onChange={() =>
                clearFieldError(
                  'otherServiceDescription',
                )
              }
            />

            <FieldError
              id="inquiry-other-service-error"
              message={
                fieldErrors.otherServiceDescription
              }
            />
          </label>
        )}

        <label>
          <span className="field-label">
            How can we help?{' '}
            <span aria-hidden="true">
              *
            </span>{' '}
            <span className="field-optional">
              Please limit links to one
            </span>
          </span>

          <textarea
            name="message"
            rows={6}
            minLength={10}
            maxLength={
              formLimits.message
            }
            required
            aria-invalid={
              Boolean(
                fieldErrors.message,
              )
            }
            aria-describedby={
              describedBy(
                'inquiry-message-counter',

                fieldErrors.message &&
                  'inquiry-message-error',
              )
            }
            onChange={(
              event,
            ) => {
              setMessageLength(
                event.target
                  .value.length,
              )

              clearFieldError(
                'message',
              )
            }}
          />

          <CharacterCounter
            id="inquiry-message-counter"
            current={
              messageLength
            }
            maximum={
              formLimits.message
            }
          />

          <FieldError
            id="inquiry-message-error"
            message={
              fieldErrors.message
            }
          />
        </label>

        <label
          className="honeypot-field"
          aria-hidden="true"
        >
          Leave this field empty

          <input
            name="website"
            type="text"
            maxLength={200}
            tabIndex={-1}
            autoComplete="off"
          />
        </label>

        <Recaptcha
          onTokenChange={(
            token,
          ) => {
            setCaptchaToken(
              token,
            )

            if (token) {
              clearFieldError(
                'recaptcha',
              )
            }
          }}
          resetKey={
            captchaResetKey
          }
          theme="dark"
        />

        <FieldError
          id="inquiry-recaptcha-error"
          message={
            fieldErrors.recaptcha
          }
        />

        <label className="form-consent">
          <input
            name="consent"
            type="checkbox"
            required
            aria-required="true"
          />

          <span>
            I agree to the{' '}
            <a href="/privacy-policy">
              Privacy Policy
            </a>{' '}
            and consent to being contacted regarding this inquiry.
          </span>
        </label>

        <button
          className="button"
          type="submit"
          disabled={
            isSubmitting ||
            !recaptchaSiteKey
          }
          aria-disabled={
            isSubmitting ||
            !recaptchaSiteKey
          }
        >
          {isSubmitting
            ? 'Sending...'
            : 'Send Inquiry'}
        </button>

        <div
          className={`form-note${
            feedback
              ? ` form-note--${feedback.type}`
              : ''
          }`}
          role={
            feedback?.type ===
            'error'
              ? 'alert'
              : 'status'
          }
          aria-live="polite"
          aria-atomic="true"
        >
          {feedback?.message}
        </div>
      </form>
    </section>
  )
}